import { useFormikContext } from 'formik';
import { motion } from 'framer-motion';
import { LegacyRef, useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FaChevronDown } from 'react-icons/fa6';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import useUserClickObserver from '../../../hooks/useUserClickObserver';
import { Country } from '../../../types/countries';

interface IAcademiaCountryCodeSelect {
  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  defaultSelectedCountryCode?: string;
}

const AcademiaCountryCodeSelect = ({
  setCountryCode,
  defaultSelectedCountryCode,
}: IAcademiaCountryCodeSelect) => {
  // integration of react hooks here
  const [searchText, setSearchText] = useState<string>('');
  const [showCountries, setShowCountries] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // integration of custom API hooks here
  const { getCountryListHandler } = useCountryActions('countryList');

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowCountries);

  // integration of formik hooks here
  const { setFieldValue } = useFormikContext();

  // setting country code based on selected country here
  useEffect(() => {
    if (getCountryListHandler.data?.is_success) {
      if (!defaultSelectedCountryCode) {
        setSelectedCountry(getCountryListHandler.data.data[0]);
        setCountryCode(getCountryListHandler.data.data[0].isd_code);

        setFieldValue('isd_code', getCountryListHandler.data.data[0].isd_code);
      } else {
        const defaultCountry = getCountryListHandler.data.data.find(
          (country) => country.iso_code === defaultSelectedCountryCode
        ) as Country;

        setSelectedCountry(defaultCountry);
        setCountryCode(defaultCountry.isd_code);

        setFieldValue('isd_code', defaultCountry.isd_code);
      }
    }
  }, [
    defaultSelectedCountryCode,
    setCountryCode,
    setFieldValue,
    getCountryListHandler.data,
  ]);

  // formatting country list based on search result declared here
  const countryList = useMemo(() => {
    if (!getCountryListHandler.data?.is_success) return [];

    return getCountryListHandler.data.data.filter((country) =>
      searchText
        ? country.country_name.toLowerCase().includes(searchText.toLowerCase())
        : country
    );
  }, [searchText, getCountryListHandler.data]);

  // handler function to select country from the list declared here
  const selectCountryHandler = (country: Country) => {
    setSelectedCountry(country);
    setCountryCode(country.isd_code);

    setFieldValue('isd_code', country.isd_code);

    setSearchText('');
    setShowCountries(false);
  };

  // rendering custom country code select field here
  return (
    <div
      tabIndex={0}
      ref={selectRef as LegacyRef<HTMLDivElement>}
      className='relative w-full md:w-fit focus:ring-0 focus:outline-none'
      onFocus={() => setShowCountries(true)}
      onBlur={() => setShowCountries(false)}
    >
      <div
        title={selectedCountry?.country_name}
        // onClick={() => setShowCountries((state) => !state)}
        className='relative peer/select border-2 rounded-md p-3 text-legal-dark-blue font-semibold duration-300 text-base border-legal-dark-blue active:scale-95 cursor-pointer w-full md:min-w-[160px]'
      >
        <div className='flex items-center gap-2'>
          <ReactCountryFlag
            style={{ width: '2em', height: '1.42em' }}
            countryCode={selectedCountry?.iso_code as string}
            svg
          />
          <p className='font-semibold text-sm'>{selectedCountry?.isd_code}</p>
        </div>

        <div
          className={`absolute right-3 top-1/2 -translate-y-1/2 duration-300 bg-base-white ${
            showCountries ? 'rotate-180' : 'rotate-0'
          }`}
        >
          <FaChevronDown className='h-6 w-6' />
        </div>
      </div>

      <motion.div
        initial={{ height: 0 }}
        animate={showCountries ? { height: 'fit-content' } : { height: 0 }}
        className='absolute left-0 top-full bg-base-white shadow-academia-sm w-full md:w-[300px] z-20 mt-2 rounded-md text-legal-dark-blue overflow-hidden text-base'
      >
        <div className='m-3'>
          <input
            tabIndex={-1}
            type='text'
            className='border-2 focus:ring-4 focus:ring-legal-dark-blue rounded-md px-3 py-2 w-full text-legal-dark-blue font-semibold duration-300 text-base border-legal-dark-blue active:scale-[0.98]'
            placeholder='Search countries...'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {countryList.length ? (
          <motion.ul
            initial={{ height: 0 }}
            animate={
              showCountries
                ? countryList.length > 5
                  ? { height: 220 }
                  : { height: 'fit-content' }
                : { height: 0 }
            }
            className='academia-scrollbar overflow-y-auto'
          >
            {countryList.map((country, index) => (
              <motion.li
                initial={{ opacity: 0 }}
                title={country.country_name}
                animate={showCountries ? { opacity: 1 } : { opacity: 0 }}
                key={index}
                onClick={() => {
                  selectCountryHandler(country);
                  selectRef.current?.blur();
                }}
                className='py-2 px-3 hover:bg-gray-200 duration-300 cursor-pointer [&:not(:last-child)]:border-b border-gray-300'
              >
                <div className='flex items-center gap-3'>
                  <ReactCountryFlag
                    style={{ width: '2em', height: '2em' }}
                    countryCode={country.iso_code}
                    svg
                  />
                  <p className='font-semibold'>{country.isd_code}</p>
                  <p className='font-semibold italic text-gray-400 text-sm'>
                    {country.country_name}
                  </p>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        ) : null}
      </motion.div>
    </div>
  );
};

export default AcademiaCountryCodeSelect;
